import { Injectable } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import {
  catchError,
  concatMap,
  filter,
  map,
  take
} from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import * as DetectionActions from './detection.actions';
import {
  selectAllDetections,
  selectDetectionEmergencyTemplate,
  selectDetectionFilters,
  selectSiteDetectionLabels
} from './detection.selectors';

import { Deserialize } from 'cerialize';
import { Detection } from '../../models/detection';
import { ApiService } from '../../services/api.service';
import { ColorTools } from '../../tools/color.tools';
import { EmergencyTemplate } from '../../models/emergency-template';
import { Comment } from '../../models/comment';
import { DetectionSummary } from '../../models/site';
import { DetectionTools } from '../../tools/detection.tools';
import { Attachment } from '../../models/attachment';
import { Color } from '../../enums/color';
import {
  selectDetectionLabelEntities
} from '../detection-label/detection-label.selectors';
import { DetectionLabelPipe } from '../../shared/pipes/detection.pipe';
import { SiteDetectionLabel } from '../../models/site-detection-label';

@Injectable()
export class DetectionEffects {

  /**
   * Init filters when:
   * - detections loaded
   * - emergency template loaded
   * - detection template loaded
   * - filters not yet initialized
   */
  initFilters$ = createEffect((): Observable<Action> =>
    this.actions$.pipe(
      ofType(
        DetectionActions.loadDetectionsSuccess,
        DetectionActions.loadDetectionEmergencyTemplateSuccess,
        DetectionActions.loadSiteDetectionLabelsSuccess
      ),
      concatLatestFrom(() => [
        this.store.select(selectAllDetections),
        this.store.select(selectDetectionEmergencyTemplate),
        this.store.select(selectSiteDetectionLabels),
        this.store.select(selectDetectionFilters),
      ]),
      filter(([_, detections, emergencyTpl, siteDetectionLabels, filters]) =>
        (detections?.length ?? 0) > 0 &&
        !filters
      ),
      map(([_, detections, emergencyTpl, siteDetectionLabels]) =>
        DetectionActions.initDetectionFilters({
          filters: DetectionTools.buildDefaultFilters(
            detections,
            emergencyTpl as EmergencyTemplate,
            siteDetectionLabels as SiteDetectionLabel[]
          )
        })
      )
    )
  );

  loadDetections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetectionActions.loadDetections),

      concatLatestFrom(() =>
        this.store.select(selectDetectionLabelEntities),
      ),

      concatMap(([
                   { organizationId, siteId },
                   detectionLabels,
                 ]) =>
        this.api.detections$(organizationId, siteId).pipe(
          map((detections) =>
            detections.map((detection: any) => {
              const obj = Deserialize(
                detection,
                Detection,
              ) as Detection;

              const translatedLabel =
                detectionLabels?.[detection.label]?.label
                ?? detection.label;

              obj.name =
                `${translatedLabel} ${
                  obj.pool?.slice(0, 4).toUpperCase() ?? ''
                }`.trim();

              obj.attachments?.forEach(
                (attachment: Attachment) => {
                  attachment.polygonHexColor =
                    obj.textColor ?? Color.PRIMARY;
                },
              );

              return obj;
            }),
          ),

          map((detections) =>
            ColorTools.setColorsForDetections(detections),
          ),

          map((detections) =>
            DetectionActions.loadDetectionsSuccess({
              detections,
            }),
          ),

          catchError(error =>
            of(
              DetectionActions.loadDetectionsFailure({
                error
              })
            )
          ),
        ),
      ),
    ),
  );

  loadDetectionEmergencyTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetectionActions.loadDetectionEmergencyTemplate),
      concatMap(({ organizationId, siteId }) =>
        this.api.detectionEmergencyTemplate$(organizationId, siteId).pipe(
          map(template => Deserialize(template, EmergencyTemplate)),
          map(template => DetectionActions.loadDetectionEmergencyTemplateSuccess({ template })),
          catchError(error => of(DetectionActions.loadDetectionEmergencyTemplateFailure({ error })))
        )
      )
    )
  );

  addDetectionEmergency$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetectionActions.addDetectionEmergency),
      concatMap(({ organizationId, siteId, detectionId, payload }) =>
        this.api.addDetectionEmergency$(organizationId, siteId, detectionId, payload).pipe(
          map(detection => ColorTools.setEmergencyColors(Deserialize(detection, Detection))),
          map(detection => ({ id: detection.id, changes: detection })),
          map(update => DetectionActions.addDetectionEmergencySuccess({ update })),
          catchError(error => of(DetectionActions.addDetectionEmergencyFailure({ error })))
        )
      )
    )
  );

  updateDetection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetectionActions.updateDetection),
      concatMap(({ organizationId, siteId, detectionId, payload }) =>
        this.api.updateDetection$(
          organizationId,
          siteId,
          detectionId,
          payload
        ).pipe(
          concatMap(detection =>
            this.detectionLabel.transform(detection.label).pipe(
              take(1),
              map(label => {
                const obj: Detection = ColorTools.setEmergencyColors(
                  Deserialize(detection, Detection)
                );

                obj.name =
                  `${label} ${
                    obj.pool?.slice(0, 4).toUpperCase() ?? ''
                  }`.trim();

                return obj;
              })
            )
          ),
          map(detection => ({
            id: detection.id,
            changes: detection,
          })),
          map(update =>
            DetectionActions.updateDetectionSuccess({ update })
          ),
          catchError(error =>
            of(
              DetectionActions.updateDetectionFailure({
                error
              })
            )
          )
        )
      )
    )
  );

  deleteDetection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetectionActions.deleteDetection),
      concatMap(({ organizationId, siteId, detectionId, payload }) =>
        this.api.updateDetection$(organizationId, siteId, detectionId, payload).pipe(
          map(detection => DetectionActions.deleteDetectionSuccess({ id: detection.id })),
          catchError(error => of(DetectionActions.deleteDetectionFailure({ error })))
        )
      )
    )
  );

  addDetection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetectionActions.addDetection),
      concatMap(({ organizationId, siteId, payload }) =>
        this.api.addDetection$(organizationId, siteId, payload).pipe(
          concatMap(detection =>
            this.detectionLabel.transform(detection.label).pipe(
              take(1),
              map(label => {
                const obj: Detection = Deserialize(
                  detection,
                  Detection
                );

                obj.name =
                  `${label} ${
                    obj.pool?.slice(0, 4).toUpperCase() ?? ''
                  }`.trim();

                return obj;
              })
            )
          ),
          map(detection =>
            ColorTools.setColorsForDetection(detection)
          ),
          map((detection: Detection) =>
            DetectionActions.addDetectionSuccess({
              detection
            })
          ),
          catchError(error =>
            of(
              DetectionActions.addDetectionFailure({
                error
              })
            )
          )
        )
      )
    )
  );

  loadSiteDetectionLabels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetectionActions.loadSiteDetectionLabels),
      concatMap(({ organizationId, siteId }) =>
        this.api.siteDetectionLabels$(organizationId, siteId).pipe(
          map(labels => Deserialize(labels, SiteDetectionLabel)),
          map(labels => DetectionActions.loadSiteDetectionLabelsSuccess({ labels })),
          catchError(error => of(DetectionActions.loadSiteDetectionLabelsFailure({ error })))
        )
      )
    )
  );


  loadDetectionComments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetectionActions.loadDetectionComments),
      concatMap(({ organizationId, siteId, detectionId }) =>
        this.api.detectionComments$(organizationId, siteId, detectionId).pipe(
          map(comments => comments.map((comment: any) => Deserialize(comment, Comment))),
          map(comments => DetectionActions.loadDetectionCommentsSuccess({ comments })),
          catchError(error => of(DetectionActions.loadDetectionCommentsFailure({ error })))
        )
      )
    )
  );

  addDetectionComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetectionActions.addDetectionComment),
      concatMap(({ organizationId, siteId, detectionId, payload }) =>
        this.api.addDetectionComment$(organizationId, siteId, detectionId, payload).pipe(
          map(comment => Deserialize(comment, Comment)),
          map(comment => DetectionActions.addDetectionCommentSuccess({ comment })),
          catchError(error => of(DetectionActions.addDetectionCommentFailure({ error })))
        )
      )
    )
  );

  loadDetectionSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetectionActions.loadDetectionSummary),
      concatMap(({ organizationId, siteId }) =>
        this.api.detectionsSummary$(organizationId, siteId).pipe(
          map(summary => Deserialize(summary, DetectionSummary)),
          map(summary => DetectionActions.loadDetectionSummarySuccess({ summary })),
          catchError(error => of(DetectionActions.loadDetectionSummaryFailure({ error })))
        )
      )
    )
  );

  generateDetectionsReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DetectionActions.generateDetectionsReport),
      concatMap(({organizationId, siteId, formats, ids}) =>
        this.api.generateDetectionsReport$(organizationId, siteId, formats, ids).pipe(
          map(email => DetectionActions.generateDetectionsReportSuccess({ email })),
          catchError(error => of(DetectionActions.generateDetectionsReportFailure({ error }))))
      ));
  });

  loadDetectionReferenceAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetectionActions.loadDetectionReferenceAttachments),
      concatMap(({ organizationId, siteId, detectionId, referenceBatchId }) =>
        this.api.loadDetectionReferenceAttachments$(organizationId, siteId, detectionId, referenceBatchId).pipe(
          map(attachments => attachments.map((attachment: any) => Deserialize(attachment, Attachment))),
          map(attachments => DetectionActions.loadDetectionReferenceAttachmentsSuccess({ attachments })),
          catchError(error => of(DetectionActions.loadDetectionReferenceAttachmentsFailure({ error })))
        )
      )
    )
  );

  setDetectionFavoriteAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetectionActions.setDetectionFavoriteAttachment),
      concatMap(({
                   organizationId,
                   siteId,
                   detectionId,
                   filename
                 }) =>
        this.api.setDetectionFavoriteAttachment$(
          organizationId,
          siteId,
          detectionId,
          filename
        ).pipe(
          map(detection => ({
            id: detectionId,
            changes: {
              favorite: detection.favorite
            }
          })),
          map(update =>
            DetectionActions.setDetectionFavoriteAttachmentSuccess({
              update
            })
          ),
          catchError(error =>
            of(
              DetectionActions.setDetectionFavoriteAttachmentFailure({
                error
              })
            )
          )
        )
      )
    )
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private api: ApiService,
    private detectionLabel: DetectionLabelPipe,
  ) {}
}
